"use client";

import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Phone, Mail, MapPin, FileUp } from "lucide-react";
import { Link } from "@/components/common/Link";
export default function SecureUpload() {
  useEffect(() => {
    const existingRobotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const previousContent = existingRobotsMeta?.content;
    let robotsMeta = existingRobotsMeta;
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.name = "robots";
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.content = "noindex, nofollow";
    return () => {
      if (existingRobotsMeta && previousContent !== undefined) {
        existingRobotsMeta.content = previousContent;
      } else if (robotsMeta?.parentNode) {
        robotsMeta.parentNode.removeChild(robotsMeta);
      }
    };
  }, []);
  return <main id="secure-client-upload" className="w-full bg-background px-6 py-20 text-foreground md:py-32">
      <section className="mx-auto w-full max-w-3xl">
        <header className="mb-16 text-center">
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-secondary">
            Document Upload Portal
          </p>

          <h1 className="mt-6 font-serif text-4xl font-bold text-primary md:text-5xl">
            Upload Documents Requested by Our Office
          </h1>

          <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
            This page is intended only for clients and other persons who have
            been instructed by our office to submit documents.
          </p>
        </header>

        <div className="rounded-lg border border-border bg-card p-8 shadow-lg md:p-12">
          <div className="mb-10">
            <h2 className="mb-6 font-serif text-2xl font-semibold text-primary">
              Before You Upload
            </h2>

            <ul className="space-y-4 text-muted-foreground">
              <li className="flex gap-3">
                <span className="mt-1.5 text-secondary">•</span>
                <span>
                  Upload documents only if requested by our office.
                </span>
              </li>

              <li className="flex gap-3">
                <span className="mt-1.5 text-secondary">•</span>
                <span>Use your full name when submitting files, and include your matter reference if one was provided by our office.</span>
              </li>

              <li className="flex gap-3">
                <span className="mt-1.5 text-secondary">•</span>
                <span>
                  Upload copies only. Do not send original documents unless
                  specifically instructed.
                </span>
              </li>

              <li className="flex gap-3">
                <span className="mt-1.5 text-secondary">•</span>
                <span>Please submit PDF, JPG, PNG, HEIC, Microsoft Word, or Microsoft Excel files only. Do not upload executable files or unsolicited ZIP archives.</span>
              </li>

              <li className="flex gap-3">
                <span className="mt-1.5 text-secondary">•</span>
                <span>
                  Uploading documents does not create an attorney-client
                  relationship.
                </span>
              </li>
            </ul>
          </div>

          <div className="border-t border-border pt-10">
            <Button asChild size="lg" className="h-14 w-full text-lg font-medium">
              <a href="https://morrielevine-my.sharepoint.com/personal/info_morrielevine_com/_layouts/15/onedrive.aspx?p=26&s=aHR0cHM6Ly9tb3JyaWVsZXZpbmUtbXkuc2hhcmVwb2ludC5jb20vOmY6L3AvaW5mby9JZ0RCRUhtYlhTcVdUNlFQT0s4dmtBZlZBY0x5ZFhla3NXTWs1TjZuQXc3bzBhZw" target="_blank" rel="noopener noreferrer">
                <FileUp className="size-5" />
                Open Document Upload Portal
              </a>
            </Button>

            <p className="mt-4 text-center text-sm text-muted-foreground">
              The document upload portal will open in a new window.
            </p>
          </div>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-border bg-accent/50 p-8">
            <h3 className="font-serif text-xl font-semibold text-primary">
              Japanese Language Assistance Available
            </h3>

            <p className="mt-4 leading-relaxed text-muted-foreground">Japanese-language assistance is available. Basic instructions and communication support are available throughout the document submission process.</p>

            <p className="mt-4 text-sm italic text-muted-foreground">
              日本語でのご案内・初期連絡にも対応しております。
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="flex items-center gap-4 rounded-lg border border-border bg-card p-4">
              <Phone className="size-5 text-secondary" />

              <Link to="tel:(954)925-9000" className="font-medium text-primary hover:underline">
                (954) 925-9000
              </Link>
            </div>

            <div className="flex items-center gap-4 rounded-lg border border-border bg-card p-4">
              <Mail className="size-5 text-secondary" />

              <Link to="mailto:info@morrielevine.com" className="font-medium text-primary hover:underline">
                info@morrielevine.com
              </Link>
            </div>

            <div className="flex items-center gap-4 rounded-lg border border-border bg-card p-4">
              <MapPin className="size-5 text-secondary" />

              <span className="text-muted-foreground">
                3300 North 29th Avenue, Suite 104, Hollywood, FL 33020
              </span>
            </div>
          </div>
        </div>
      </section>
    </main>;
}